import { useState, useCallback, useMemo, useEffect, useRef } from 'react';
import { useDashboardUI } from '../../context';

const DEBOUNCE_MS = 500;

export const useTemplateFilters = () => {
  const { templateFilters, setTemplateFilters } = useDashboardUI();
  const [localSearch, setLocalSearch] = useState(templateFilters.search || '');
  const searchTimeoutRef = useRef(null);
  const isFirstRenderRef = useRef(true);

  useEffect(() => {
    if (isFirstRenderRef.current) {
      isFirstRenderRef.current = false;
      return;
    }

    if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);

    searchTimeoutRef.current = setTimeout(() => {
      setTemplateFilters({ search: localSearch });
    }, DEBOUNCE_MS);

    return () => {
      if (searchTimeoutRef.current) clearTimeout(searchTimeoutRef.current);
    };
  }, [localSearch, setTemplateFilters]);

  const handleSectionChange = useCallback(
    (value) => {
      setTemplateFilters({ sectionId: value || '' });
    },
    [setTemplateFilters]
  );

  const handleCategoryChange = useCallback(
    (value) => {
      setTemplateFilters({ categoryId: value || '' });
    },
    [setTemplateFilters]
  );

  const resetFilters = useCallback(() => {
    setLocalSearch('');
    setTemplateFilters({ search: '', sectionId: '', categoryId: '' });
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
      searchTimeoutRef.current = null;
    }
  }, [setTemplateFilters]);

  const hasActiveFilters = useMemo(
    () =>
      localSearch.trim() !== '' ||
      (templateFilters.sectionId || '').trim() !== '' ||
      (templateFilters.categoryId || '').trim() !== '',
    [localSearch, templateFilters.sectionId, templateFilters.categoryId]
  );

  return {
    localSearch,
    setLocalSearch,
    templateFilters,
    handleSectionChange,
    handleCategoryChange,
    resetFilters,
    hasActiveFilters,
  };
};
